import React, { useState, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { API_URL } from '../../utils/api';

export default function ConsentWall({ user, onAccepted }) {
  const { theme } = useContext(ThemeContext);

  const [leyoTerminos,       setLeyoTerminos]       = useState(false);
  const [leyoCondPublicador, setLeyoCondPublicador] = useState(false);
  const [leyoPrivacidad,     setLeyoPrivacidad]     = useState(false);
  const [leyoCookies,        setLeyoCookies]        = useState(false);
  const [aceptaTerminos,     setAceptaTerminos]     = useState(false);
  const [aceptaPriv,         setAceptaPriv]         = useState(false);
  const [loading,            setLoading]            = useState(false);
  const [error,              setError]              = useState('');

  const puedeEnviar = aceptaTerminos && aceptaPriv;

  const handleAceptar = async () => {
    if (!puedeEnviar) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('trastero_token');
      const res = await fetch(`${API_URL}/api/auth/consentimiento`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ acepta_terminos: true, acepta_privacidad: true, version_legal: '1.0' }),
      });
      if (!res.ok) { setError('Error al guardar. Inténtalo de nuevo.'); setLoading(false); return; }
      onAccepted();
    } catch {
      setError('Error de conexión. Inténtalo de nuevo.');
      setLoading(false);
    }
  };

  const acc = theme.accent;
  const bg  = theme.modalBg || theme.background;

  const docLink = (href, label, onClickFn) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClickFn}
      style={{ color: acc, fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      📄 {label}
    </a>
  );

  const bloque1Ok = leyoTerminos && leyoCondPublicador;
  const bloque2Ok = leyoPrivacidad && leyoCookies;

  const checkboxStyle = (activo) => ({
    display: 'flex', alignItems: 'flex-start', gap: 10,
    cursor: activo ? 'pointer' : 'not-allowed',
    fontSize: 13,
    color: activo ? acc + 'cc' : acc + '44',
    opacity: activo ? 1 : 0.5,
    marginTop: 6,
  });

  return (
    /* Overlay fijo — cubre toda la pantalla, sin posibilidad de cerrar */
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: bg,
        border: `1px solid ${acc}44`,
        borderRadius: 16,
        padding: '32px 28px',
        maxWidth: 520,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}>
        <div>
          <h2 style={{ color: acc, margin: '0 0 6px', fontSize: 20, fontWeight: 800 }}>
            Actualización de condiciones
          </h2>
          <p style={{ color: acc + 'aa', fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            Hola <strong style={{ color: acc }}>{user?.name || user?.nombre}</strong>,
            para continuar usando Trastero debes leer y aceptar los documentos legales.
            No podrás acceder hasta completar este paso.
          </p>
        </div>

        {/* Bloque 1 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={{ color: acc + 'bb', fontSize: 12, margin: 0, fontWeight: 600 }}>
            1. Abre y lee estos documentos:
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingLeft: 8 }}>
            {docLink('/legal/terminos',               'Términos y Condiciones',     () => setLeyoTerminos(true))}
            {docLink('/legal/condiciones-publicador', 'Condiciones del Publicador', () => setLeyoCondPublicador(true))}
          </div>
          <label style={checkboxStyle(bloque1Ok)}>
            <input
              type="checkbox"
              checked={aceptaTerminos}
              disabled={!bloque1Ok}
              onChange={e => setAceptaTerminos(e.target.checked)}
              style={{ marginTop: 2, accentColor: acc, flexShrink: 0 }}
            />
            <span>
              He leído y acepto los Términos y Condiciones y las Condiciones del Publicador
              {!bloque1Ok && (
                <span style={{ display: 'block', fontSize: 11, color: acc + '77', marginTop: 2 }}>
                  Abre ambos documentos para activar esta casilla
                </span>
              )}
            </span>
          </label>
        </div>

        {/* Bloque 2 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={{ color: acc + 'bb', fontSize: 12, margin: 0, fontWeight: 600 }}>
            2. Abre y lee estos documentos:
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingLeft: 8 }}>
            {docLink('/legal/privacidad', 'Política de Privacidad', () => setLeyoPrivacidad(true))}
            {docLink('/legal/cookies',    'Política de Cookies',    () => setLeyoCookies(true))}
          </div>
          <label style={checkboxStyle(bloque2Ok)}>
            <input
              type="checkbox"
              checked={aceptaPriv}
              disabled={!bloque2Ok}
              onChange={e => setAceptaPriv(e.target.checked)}
              style={{ marginTop: 2, accentColor: acc, flexShrink: 0 }}
            />
            <span>
              He leído y acepto la Política de Privacidad y la Política de Cookies
              {!bloque2Ok && (
                <span style={{ display: 'block', fontSize: 11, color: acc + '77', marginTop: 2 }}>
                  Abre ambos documentos para activar esta casilla
                </span>
              )}
            </span>
          </label>
        </div>

        {error && (
          <p style={{ color: '#f87171', fontSize: 13, margin: 0 }}>{error}</p>
        )}

        <button
          onClick={handleAceptar}
          disabled={!puedeEnviar || loading}
          style={{
            background: puedeEnviar ? acc : acc + '44',
            color: puedeEnviar ? '#fff' : acc + '88',
            border: 'none',
            borderRadius: 10,
            padding: '12px 0',
            fontWeight: 700,
            fontSize: 15,
            cursor: puedeEnviar ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s',
            width: '100%',
          }}
        >
          {loading ? 'Guardando...' : 'Confirmar y continuar'}
        </button>

        <p style={{ color: acc + '55', fontSize: 11, margin: 0, textAlign: 'center' }}>
          Si no aceptas, puedes cerrar sesión desde el menú. Sin aceptación no es posible continuar.
        </p>
      </div>
    </div>
  );
}
